/**
 * Deploy panel component for the workspace — the whole "Pipeline" modal.
 *
 * Allows users to trigger deployments, view deployment history,
 * and stream build logs in real-time.
 *
 * Visual contract: the panel is ONE scrolling view (no tabs), in the order a
 * user works through shipping — Repository (connect one git method, then push),
 * a short pointer to the CI/CD `setup-ci` script, Custom domain, then Deploy.
 * All colour comes from `getClassMap()` theme tokens (never hardcoded hex).
 *
 * No commit-message input on the Deploy button (P-01): the deploy flow never
 * makes a git commit — the old input was a pure history label wearing git
 * semantics, which read as confusing. Old rows that carry one still render it.
 *
 * The panel opens while the dev sandbox is stopped or sleeping (D6). Almost
 * everything it shows is control-plane or production state — deployment history,
 * the live production URL, build logs, git credentials, custom domains and their
 * certificates — and the live URL points at the deployed instance, which does
 * not depend on the dev sandbox (Fly parks it when idle and wakes it on request),
 * so the panel is at its most useful exactly when the dev sandbox is not. Only
 * the two actions that touch the container are gated on `sandboxStatus`: Deploy
 * and git push. Each offers an in-panel wake button (when the workspace passes
 * `onWakeSandbox`) rather than dead-ending, because both build/read from the
 * sandbox.
 */

import type { JSX } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { OAuthProviderLogo } from '@molecule/app-oauth-logos-react'
import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Icon, Switch, Tooltip } from '@molecule/app-ui-react'

import { API_BASE_URL } from '../config.js'
import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import { emitNotification } from '../notifications.js'
import { onResourceInvalidated } from '../utilities/resource-invalidation.js'
import { formatLimit } from '../utilities/usage-utilities.js'
import { DomainConfig } from './DomainConfig.js'

interface Deployment {
  id: string
  target: string
  status: 'pending' | 'building' | 'deploying' | 'ready' | 'failed' | 'cancelled'
  url: string | null
  buildLog: string
  error: string | null
  commitMessage: string | null
  createdAt: string
  completedAt: string | null
}

interface DeployPanelProps {
  projectId: string
  sandboxStatus: string
  /**
   * False for read-only project viewers: every mutating control (deploy,
   * cancel, git push/pull, SSH key, provider connect, domains) is hidden —
   * the server 403s those routes below editor anyway. Status/history stay.
   */
  canEdit?: boolean
  /**
   * Wake/boot the dev sandbox — the same action the workspace shell's "Wake up"
   * button runs (`POST /projects/:id/sandbox/start`). When provided, the Deploy
   * and Push buttons are replaced by an in-panel wake button while the sandbox
   * is asleep/stopped, so the user never has to leave the modal to bring the
   * build machine up. Optional so the panel still renders (with the disabled
   * buttons + a "start it from the workspace" caption) when no wake path is wired.
   */
  onWakeSandbox?: () => void
  /**
   * Hand a failed deploy to the AI: called with a ready-to-send prompt (the
   * build errors + "fix and redeploy") when the user clicks "Fix with Synthase"
   * on a failed deployment. The workspace owns the chat, so it starts a fresh
   * conversation seeded with this prompt. Optional — the button only renders
   * when provided.
   */
  onFixWithSynthase?: (prompt: string) => void
}

type GitProvider = 'github' | 'gitlab'

/**
 * The live in-IDE Deploy button drives ONLY the first-party `molecule` runtime
 * (Deploy/index.ts — product decision 2026-07-07: molecule hosts on its own
 * servers only). Third-party CI/CD is offered as the `setup-ci` script (see the
 * CI note below), which writes a GitHub Actions / GitLab CI workflow into the
 * user's own repo.
 */
const LIVE_DEPLOY_TARGET = 'molecule'

/**
 * The slice of `GET /account/usage` the panel uses: the owner's effective tier
 * limits + plan, so the deploy target caption can label the free default's
 * limits accurately and the custom-domain section can gate on `maxCustomDomains`
 * — read from the server, never guessed.
 */
interface AccountUsage {
  plan: string | null
  /** `anonymous` | `free` | `pro` | `team` — the paid check reads this, not
   * the raw key, so every paid key shape (stripe/team/apple/google) buckets
   * right. Absent on old cached responses → treated as free (locked toggle). */
  planCategory?: string
  limits: {
    maxActiveDeployments: number
    maxCustomDomains: number
  }
}

/**
 * A structured deploy error surfaced to the user. `cta` chooses the call to
 * action: `'signup'` for a guest who must register, `'upgrade'` for a capped
 * user who must move to a higher plan, `null` for a plain failure with no
 * upgrade path.
 */
interface DeployError {
  message: string
  cta: 'signup' | 'upgrade' | null
}

interface GitProviderStatus {
  provider: GitProvider
  configured: boolean
  connected: boolean
  scopes: string[]
  originUrl: string | null
}

interface GitCredentialStatus {
  providers: GitProviderStatus[]
  ssh: { connected: boolean; fingerprint: string | null; originUrl: string | null }
}

const GIT_PROVIDER_LABELS: Record<GitProvider, string> = { github: 'GitHub', gitlab: 'GitLab' }

/**
 * How the user authenticates to push. Only ONE is used per project, so the
 * panel shows a compact selector and renders just the chosen method — GitHub or
 * GitLab OAuth, or a pasted SSH key — instead of stacking all three.
 */
type AuthMethod = 'github' | 'gitlab' | 'ssh'

/** The auth methods in selector order. */
const AUTH_METHODS: readonly AuthMethod[] = ['github', 'gitlab', 'ssh']

/** English fallbacks for each deployment status badge (rendered through `t()`). */
const STATUS_DEFAULT_LABELS: Record<Deployment['status'], string> = {
  pending: 'Pending',
  building: 'Building',
  deploying: 'Deploying',
  ready: 'Ready',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

/**
 * Shared chip/pill shape for the deploy-history row (status, time, description).
 * One shape for all three so they sit on a common baseline at a common size —
 * only the color (via className) and background tint differ.
 */
const pillStyle: JSX.IntrinsicElements['span']['style'] = {
  fontSize: 11,
  height: 20,
  boxSizing: 'border-box',
  padding: '0 8px',
  borderRadius: 100,
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
}

export function DeployPanel({
  projectId,
  sandboxStatus,
  canEdit,
  onWakeSandbox,
  onFixWithSynthase,
}: DeployPanelProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // Phones/touch: 16px input font (iOS zooms any focusable field below 16px)
  // and the 40px touch floor on the auth-method selector.
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')
  const inputFontSize = isMobile || coarse ? 16 : 13

  // The pipeline modal is now ONE scrolling view (Repository → CI/CD note →
  // Custom domain → Deploy), not a tab set — so no usePanelModalTabs here.

  // Which git auth method the user is configuring — only one is used per
  // project, so we show a compact selector and render just the chosen one.
  const [authMethod, setAuthMethod] = useState<AuthMethod>('github')
  // Once true, an auto-select from loaded git status never overrides the user's
  // pick.
  const authMethodTouched = useRef(false)
  const [deployments, setDeployments] = useState<Deployment[]>([])
  const [deploying, setDeploying] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  // CICD1: a deploy that the backend rejects (402/403 with requiresUpgrade /
  // requiresSignup / a limitType) is surfaced here with a CTA — never swallowed
  // into console.error, which left a capped free user staring at a dead button.
  const [deployError, setDeployError] = useState<DeployError | null>(null)
  // The owner's effective tier limits/plan (GET /account/usage) — drives the
  // free-default caption, the custom-domain gate and the badge toggle's
  // paid/free split. `null` until loaded.
  const [usage, setUsage] = useState<AccountUsage | null>(null)
  // The per-app "Built with Molecule.dev" badge choice
  // (projects.settings.attributionBadge; the deploy re-decides it each time —
  // runtime/attribution.ts). null until the project row loads; the default is
  // ON, so a failed load just means the toggle shows the platform default.
  const [badgeEnabled, setBadgeEnabled] = useState<boolean | null>(null)
  const [badgeSaving, setBadgeSaving] = useState(false)
  // Where production deploys are hosted (projects.settings.hostingTarget —
  // api runtime/hosting-target.ts): 'auto' picks static files for a static
  // site and a machine for anything with a server. null until the row loads.
  const [hostingTarget, setHostingTarget] = useState<'auto' | 'static' | 'machine' | null>(null)
  const [hostingSaving, setHostingSaving] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // Build logs live on the DETAIL route only (the list omits the multi-MB
  // column); fetched for the expanded row and re-fetched while it is active.
  const [logsById, setLogsById] = useState<Record<string, string>>({})

  // Git push (GIT1)
  const [gitStatus, setGitStatus] = useState<GitCredentialStatus | null>(null)
  const [sshKeyInput, setSshKeyInput] = useState('')
  const [savingSsh, setSavingSsh] = useState(false)
  const [originUrl, setOriginUrl] = useState('')
  // Where the current origin value came from, so the two async defaults
  // (remembered git-credentials vs. imported-repo URL) can't clobber each other
  // or a user-typed value regardless of which request resolves first (P-04):
  // user > remembered > import.
  const originSourceRef = useRef<'user' | 'remembered' | 'import' | null>(null)
  // Phase 10 multi-repo: the workspace's pushable repo dirs (the scaffolded app
  // + imported repos), read from the SANDBOX via git-workspace-repos — the same
  // list the push endpoint validates repoDir against. Never inferred from
  // project settings: those record the dir the platform meant to scaffold,
  // which for an imported project was never created at all.
  const [workspaceRepos, setWorkspaceRepos] = useState<string[]>([])
  const [pushRepoDir, setPushRepoDir] = useState('')
  const [pushing, setPushing] = useState(false)
  const [pushOutput, setPushOutput] = useState<string | null>(null)
  // Set when a push is rejected because the remote moved (non-fast-forward) —
  // recoverable, so the panel offers "Resolve with Synthase" instead of a dead
  // error.
  const [pushRejected, setPushRejected] = useState(false)
  // A ready-to-send prompt for "Fix with Synthase" on a push that FAILED for a
  // fixable reason (auth, a bad branch, a missing repo, secret files in the
  // tree) — distinct from the non-fast-forward `pushRejected` case, which has
  // its own tailored "Resolve" recovery. Null = no fix button.
  const [pushFixPrompt, setPushFixPrompt] = useState<string | null>(null)
  // Pull (GITPULL1) — the mirror of push: bring the remote INTO the sandbox.
  const [pulling, setPulling] = useState(false)
  const [pullOutput, setPullOutput] = useState<string | null>(null)
  // A ready-to-send prompt for "Resolve with Synthase" when a pull hits a
  // conflict or a divergence the panel can't resolve on its own. Null = no button.
  const [pullFixPrompt, setPullFixPrompt] = useState<string | null>(null)

  // Status → semantic theme-token text class. The badge tint is derived from this
  // colour via `color-mix(currentColor)`, so every status shares one treatment and
  // no hex is hardcoded.
  const statusColorClass: Record<Deployment['status'], string> = {
    pending: cm.textWarning,
    building: cm.textPrimary,
    deploying: cm.textPrimary,
    ready: cm.textSuccess,
    failed: cm.textError,
    cancelled: cm.textSubtle,
  }

  // Load deployments
  const loadDeployments = useCallback(async () => {
    try {
      const res = await http.get<{ data: Deployment[] }>(`/projects/${projectId}/deployments`)
      setDeployments(res.data.data ?? [])
      setLoadError(null)
    } catch (error) {
      console.error('Failed to load deployments', error)
      setLoadError(
        t('deploy.loadError', undefined, { defaultValue: 'Failed to load deployments.' }),
      )
    }
  }, [projectId, http])

  useEffect(() => {
    loadDeployments()
  }, [loadDeployments])

  // Live cross-client refresh: a deploy started/progressed/finished elsewhere
  // (a teammate, Synthase's /deploy, the server's stranded-deploy reaper) lands
  // immediately instead of waiting for the next poll tick.
  useEffect(
    () => onResourceInvalidated('deployments', () => void loadDeployments()),
    [loadDeployments],
  )

  // Load the owner's effective tier limits + plan once, for the free-default
  // caption and the custom-domain gate. Best-effort: a failure leaves the
  // caption generic and the domain section in its loading state.
  useEffect(() => {
    let cancelled = false
    http
      .get<AccountUsage>('/account/usage')
      .then((res) => {
        // Only adopt a well-formed rollup (carries `limits`); otherwise leave the
        // caption generic and the domain gate in its loading state.
        if (!cancelled && res.data?.limits) setUsage(res.data)
      })
      .catch((error) => {
        // Non-fatal — the panel still works; the caption/gate just stay generic.
        console.error('Failed to load account usage', error)
      })
    return () => {
      cancelled = true
    }
  }, [http])

  // Stream the expanded deployment's build log: the detail route carries the
  // log the list omits; poll it on the same cadence while that deploy is active.
  const expandedDep = deployments.find((d) => d.id === expandedId)
  const expandedActive = expandedDep
    ? ['pending', 'building', 'deploying'].includes(expandedDep.status)
    : false
  useEffect(() => {
    if (!expandedId) return
    let cancelled = false
    const fetchLog = async (): Promise<void> => {
      try {
        const res = await http.get<{ data: Deployment }>(
          `/projects/${projectId}/deployments/${expandedId}`,
        )
        const log = res.data.data?.buildLog ?? ''
        if (!cancelled) {
          setLogsById((prev) => (prev[expandedId] === log ? prev : { ...prev, [expandedId]: log }))
        }
      } catch (error) {
        // Non-fatal — the row keeps showing whatever log it already had.
        console.error('Failed to load deployment log', error)
      }
    }
    fetchLog()
    if (!expandedActive) {
      return () => {
        cancelled = true
      }
    }
    const timer = setInterval(fetchLog, 3000)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [expandedId, expandedActive, projectId, http])

  // Poll deployments the WHOLE time the panel is open (fast while a deploy is
  // active, slower when idle) — so a deploy triggered elsewhere (Synthase, the
  // `/deploy` command, another tab) appears here on its own, without the user
  // closing and reopening the modal.
  useEffect(() => {
    const hasActive = deployments.some(
      (d) => d.status === 'pending' || d.status === 'building' || d.status === 'deploying',
    )
    const timer = setInterval(loadDeployments, hasActive ? 3000 : 8000)
    return () => clearInterval(timer)
  }, [deployments, loadDeployments])

  // Trigger deployment
  const handleDeploy = useCallback(async () => {
    if (deploying) return
    setDeploying(true)
    setDeployError(null)
    try {
      const created = await http.post<{ data?: { id?: string } }>(
        `/projects/${projectId}/deployments`,
        { target: LIVE_DEPLOY_TARGET },
      )
      // Expand the new deployment so its build log streams into view.
      const newId = created.data?.data?.id
      if (newId) setExpandedId(newId)
      await loadDeployments()
    } catch (error) {
      // CICD1: the backend caps deploys per tier and returns a structured error
      // (402/403 with requiresUpgrade / requiresSignup / limitType). Surface its
      // message + a CTA to Pricing/signup instead of swallowing it — same shape
      // the create-flow handles on the Dashboard.
      const data = (
        error as {
          response?: {
            data?: {
              error?: string
              limitType?: string
              requiresUpgrade?: boolean
              requiresSignup?: boolean
            }
          }
        }
      ).response?.data
      if (data && (data.limitType || data.requiresUpgrade || data.requiresSignup)) {
        setDeployError({
          message:
            data.error ??
            t('deploy.limitReached', undefined, {
              defaultValue: 'Deployment limit reached for your plan.',
            }),
          cta: data.requiresSignup ? 'signup' : 'upgrade',
        })
      } else {
        setDeployError({
          message:
            data?.error ??
            t('deploy.failed', undefined, {
              defaultValue: 'Failed to start the deployment. Please try again.',
            }),
          cta: null,
        })
      }
    }
    setDeploying(false)
  }, [projectId, http, deploying, loadDeployments, t])

  // Cancel deployment
  const handleCancel = useCallback(
    async (depId: string) => {
      try {
        await http.delete(`/projects/${projectId}/deployments/${depId}`)
        await loadDeployments()
      } catch (error) {
        console.error('Failed to cancel deployment', error)
      }
    },
    [projectId, http, loadDeployments],
  )

  // "Fix with Synthase" on a FAILED deploy — fetch the full build log (the real
  // tsc/build errors, which the list route omits) and hand the AI a ready-to-send
  // prompt via the workspace. The workspace opens a fresh chat seeded with it.
  const handleFixDeploy = useCallback(
    async (dep: Deployment) => {
      if (!onFixWithSynthase) return
      let buildLog = dep.buildLog || ''
      try {
        const res = await http.get<{ data: Deployment }>(
          `/projects/${projectId}/deployments/${dep.id}`,
        )
        buildLog = res.data.data?.buildLog || buildLog
      } catch (error) {
        // Non-fatal — fall back to the terse `error` if the detail read fails.
        console.error('Failed to load deployment log for fix', error)
      }
      // Keep the seed prompt focused: the build errors (tail) + the instruction.
      const details = (buildLog || dep.error || '').slice(-4000).trim()
      const prompt =
        `The production deploy failed. Build errors:\n\n${details}\n\n` +
        `Fix the cause of these errors in the code, then deploy again (use the deploy tool). ` +
        `Repeat until it deploys successfully.`
      onFixWithSynthase(prompt)
    },
    [projectId, http, onFixWithSynthase],
  )

  // --- Git push (GIT1) ---

  /**
   * Hand a rejected push to Synthase: seed a chat asking it to recover with the
   * `git_push` tool, explicitly telling it to ask the user rebase-vs-overwrite
   * before touching the remote.
   */
  const handleResolvePush = useCallback(() => {
    if (!onFixWithSynthase) return
    const target = originUrl.trim()
    const prompt =
      `My push to ${target || 'the git remote'}${pushRepoDir ? ` (repo "${pushRepoDir}")` : ''} was ` +
      `rejected because the remote has commits my sandbox does not have.\n\n` +
      `Use the git_push tool to recover. First ask me whether to rebase (replay my commits on top of ` +
      `the remote) or force-with-lease (overwrite the remote with my version) — don't overwrite ` +
      `anything without my OK — then push.`
    onFixWithSynthase(prompt)
  }, [onFixWithSynthase, originUrl, pushRepoDir])

  const loadGitStatus = useCallback(async () => {
    try {
      const res = await http.get<GitCredentialStatus>(`/projects/${projectId}/git-credentials`)
      setGitStatus(res.data)
      // Pre-fill the origin field from a remembered origin, if any. A remembered
      // origin outranks the imported-repo default (P-04) but never overwrites
      // what the user typed — race-safe whichever request resolves first.
      const remembered =
        res.data.providers?.find((p) => p.originUrl)?.originUrl ?? res.data.ssh?.originUrl
      if (remembered) {
        setOriginUrl((prev) => {
          if (prev && originSourceRef.current !== 'import') return prev
          originSourceRef.current = 'remembered'
          return remembered
        })
      }
    } catch (error) {
      console.error('Failed to load git status', error)
    }
    // Which repos the sandbox actually holds. Refreshed alongside credentials
    // (mount, OAuth return, after a push) so a workspace that gained a repo —
    // or booted since the panel opened — picks it up without a reload.
    // This ONE read genuinely needs the container (D6): the panel opens while the
    // sandbox sleeps, and asking a sleeping sandbox for its repo list only buys a
    // failed request. The effect re-runs when it starts, so the picker fills in
    // then.
    if (sandboxStatus !== 'running') return
    try {
      const res = await http.get<{ repos?: string[] }>(`/projects/${projectId}/git-workspace-repos`)
      const repos = (res.data.repos ?? []).filter((dir) => typeof dir === 'string')
      setWorkspaceRepos(repos)
      // Keep a valid selection: a stale one would be refused by the push.
      setPushRepoDir((prev) => (prev && repos.includes(prev) ? prev : (repos[0] ?? '')))
    } catch (error) {
      console.error('Failed to load workspace repos', error)
    }
  }, [projectId, http, sandboxStatus])

  // Load git status on mount so the Repository section is ready immediately.
  useEffect(() => {
    loadGitStatus()
  }, [loadGitStatus])

  // Default the auth-method selector to whatever the user already set up — a
  // connected provider, else an SSH key — so the compact view opens on the
  // relevant method. Never overrides a manual pick (authMethodTouched).
  useEffect(() => {
    if (authMethodTouched.current || !gitStatus) return
    const connectedProvider = gitStatus.providers?.find((p) => p.connected)?.provider
    if (connectedProvider) setAuthMethod(connectedProvider)
    else if (gitStatus.ssh?.connected) setAuthMethod('ssh')
  }, [gitStatus])

  // Detect the OAuth redirect-back (?gitAuth=…&gitStatus=…), surface a notice,
  // refresh status, and strip the params from the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const gitAuth = params.get('gitAuth')
    const status = params.get('gitStatus')
    if (!gitAuth || !status) return

    const providerLabel = GIT_PROVIDER_LABELS[gitAuth as GitProvider] ?? gitAuth
    if (status === 'connected') {
      emitNotification(
        t(
          'git.connectedNotice',
          { provider: providerLabel },
          {
            defaultValue: `Connected to ${providerLabel}.`,
          },
        ),
        'info',
      )
      loadGitStatus()
    } else if (status === 'unconfigured') {
      emitNotification(
        t(
          'git.unconfigured',
          { provider: providerLabel },
          {
            defaultValue: `This server has no ${providerLabel} app configured. Ask the operator to set up a git provider app.`,
          },
        ),
        'warning',
      )
    } else {
      emitNotification(
        t(
          'git.connectError',
          { provider: providerLabel },
          {
            defaultValue: `Could not connect to ${providerLabel}. Please try again.`,
          },
        ),
        'error',
      )
    }

    params.delete('gitAuth')
    params.delete('gitStatus')
    const query = params.toString()
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    )
  }, [t, loadGitStatus])

  // Connect a provider — top-level navigation so the API initiate route runs
  // with the session cookie and then redirects to the provider consent screen.
  const handleConnect = useCallback(
    (provider: GitProvider) => {
      window.location.href = `${API_BASE_URL}/projects/${projectId}/git-auth/${provider}`
    },
    [projectId],
  )

  // Deletes the caller's own stored credential (and asks the provider to revoke an OAuth token).
  const handleDisconnect = useCallback(
    async (which: GitProvider | 'ssh') => {
      try {
        await http.delete(`/projects/${projectId}/git-credentials/${which}`)
        emitNotification(
          t('git.disconnected', undefined, {
            defaultValue: 'Disconnected. Your saved credential was deleted.',
          }),
          'info',
        )
        await loadGitStatus()
      } catch (error) {
        console.error('Failed to disconnect git credential', error)
        emitNotification(
          t('git.disconnectFailed', undefined, {
            defaultValue: 'Could not disconnect. Please try again.',
          }),
          'error',
        )
      }
    },
    [projectId, http, t, loadGitStatus],
  )

  const handleSaveSshKey = useCallback(async () => {
    if (savingSsh || !sshKeyInput.trim()) return
    setSavingSsh(true)
    try {
      await http.put(`/projects/${projectId}/git-ssh-key`, { privateKey: sshKeyInput })
      setSshKeyInput('')
      emitNotification(t('git.sshSaved', undefined, { defaultValue: 'SSH key saved.' }), 'info')
      await loadGitStatus()
    } catch (error) {
      console.error('Failed to save SSH key', error)
      emitNotification(
        t('git.sshInvalid', undefined, {
          defaultValue: 'Could not parse that SSH key. Paste a valid PEM private key.',
        }),
        'error',
      )
    }
    setSavingSsh(false)
  }, [projectId, http, savingSsh, sshKeyInput, t, loadGitStatus])

  useEffect(() => {
    let cancelled = false
    http
      .get<{
        settings?: { importSources?: Array<{ repoName?: string; url?: string }> }
      }>(`/projects/${projectId}`)
      .then((res) => {
        if (cancelled) return
        const settings = res.data.settings ?? {}
        // P-04: if the user imported a repo, its clone URL is the natural push
        // origin. Uploaded tarballs use 'upload:<name>' — not a git remote.
        // Fills only when the field is otherwise empty (remembered/user wins).
        const importUrl = Array.isArray(settings.importSources)
          ? settings.importSources
              .map((source) => source?.url)
              .find(
                (url): url is string =>
                  typeof url === 'string' && url.length > 0 && !url.startsWith('upload:'),
              )
          : undefined
        if (importUrl) {
          setOriginUrl((prev) => {
            if (prev) return prev
            originSourceRef.current = 'import'
            return importUrl
          })
        }
        // The badge setting rides the same read. The column reaches this code
        // as a parsed object (or, defensively, a JSON string); only an explicit
        // false is OFF — anything unreadable means the default (on).
        const rawSettings = res.data.settings
        let bag: Record<string, unknown> | null = null
        if (typeof rawSettings === 'string') {
          try {
            bag = JSON.parse(rawSettings) as Record<string, unknown>
          } catch (_parseError) {
            bag = null
          }
        } else if (rawSettings && typeof rawSettings === 'object') {
          bag = rawSettings as Record<string, unknown>
        }
        if (!cancelled) setBadgeEnabled(bag?.attributionBadge !== false)
        const storedHosting = bag?.hostingTarget
        if (!cancelled) {
          setHostingTarget(
            storedHosting === 'static' || storedHosting === 'machine' ? storedHosting : 'auto',
          )
        }
      })
      .catch((_error) => {
        // Only the origin pre-fill and the toggle default (on) stand on this
        // read; neither failure is actionable in the panel.
      })
    return () => {
      cancelled = true
    }
  }, [projectId, http])

  const handlePush = useCallback(async () => {
    if (pushing || !originUrl.trim()) return
    setPushing(true)
    setPushOutput(null)
    setPushRejected(false)
    setPushFixPrompt(null)
    try {
      const res = await http.post<{ ok: boolean; pushed: boolean; output?: string }>(
        `/projects/${projectId}/git-push`,
        {
          origin: originUrl.trim(),
          // Always sent when known — not only when the picker is visible. The
          // workspace root is a repo too, so "one app repo" is still a
          // multi-root workspace to git; naming the target removes the guess.
          ...(pushRepoDir ? { repoDir: pushRepoDir } : {}),
        },
      )
      setPushOutput(res.data.output ?? '')
      emitNotification(t('git.pushed', undefined, { defaultValue: 'Pushed successfully.' }), 'info')
      await loadGitStatus()
    } catch (error) {
      console.error('Push failed', error)
      const data = (
        error as {
          response?: {
            data?: {
              output?: string
              error?: string
              errorKey?: string
              findings?: { path: string; rule: string }[]
            }
          }
        }
      ).response?.data
      // SEC1: the server independently scans the committed tree before pushing
      // and refuses if secret files/keys would leak to the public remote. Show
      // the offending files and how to remove them — not a generic failure.
      if (data?.errorKey === 'git.error.secretDetected') {
        const files = [...new Set((data.findings ?? []).map((f) => f.path))].join(', ')
        const message = t(
          'git.secretBlocked',
          { files },
          {
            defaultValue:
              'Push blocked — secret files or keys were found in your committed history ({{files}}) ' +
              'and would be exposed on a public remote. Remove them from git (add to .gitignore, ' +
              'then run `git rm --cached <file>`) and commit before pushing.',
          },
        )
        setPushOutput(message)
        setPushFixPrompt(
          `My git push was blocked because these committed files look like secrets: ${files}. ` +
            `Remove them from git tracking (add them to .gitignore, then git rm --cached), commit, ` +
            `then push again with the git_push tool.`,
        )
        emitNotification(message, 'error')
      } else if (data?.errorKey === 'git.error.originHostMismatch') {
        const message = t('git.originMismatch', undefined, {
          defaultValue:
            'That origin host is not one of your connected GitHub/GitLab providers. ' +
            'Push only to your own repository, or use an SSH origin.',
        })
        setPushOutput(message)
        emitNotification(message, 'error')
      } else if (data?.errorKey === 'git.error.pushRejected') {
        // The remote moved (non-fast-forward). Recoverable — Synthase can rebase
        // or force-with-lease after asking how you want to handle it.
        const message = t('git.pushRejected', undefined, {
          defaultValue:
            'Push rejected — the remote has changes your sandbox does not. It needs to be rebased ' +
            'onto the remote, or the remote overwritten. Synthase can handle this for you.',
        })
        setPushOutput(data?.output ?? message)
        setPushRejected(true)
        emitNotification(message, 'error')
      } else {
        const output = data?.output ?? data?.error ?? null
        setPushOutput(output)
        setPushFixPrompt(
          `My git push${originUrl.trim() ? ` to ${originUrl.trim()}` : ''} failed.` +
            (output ? `\n\nOutput:\n${output}` : '') +
            `\n\nDiagnose the cause and fix it, then push again with the git_push tool. If it is an ` +
            `authentication or connection problem, tell me exactly what I need to do.`,
        )
        emitNotification(t('git.pushFailed', undefined, { defaultValue: 'Push failed.' }), 'error')
      }
    }
    setPushing(false)
  }, [projectId, http, pushing, originUrl, pushRepoDir, t, loadGitStatus])

  const handlePull = useCallback(async () => {
    if (pulling || !originUrl.trim()) return
    setPulling(true)
    setPullOutput(null)
    setPullFixPrompt(null)
    try {
      const res = await http.post<{ ok: boolean; pulled: boolean; output?: string }>(
        `/projects/${projectId}/git-pull`,
        {
          origin: originUrl.trim(),
          // Same repo-targeting as push — the workspace root is a repo too, so
          // naming the target removes the multi-root guess.
          ...(pushRepoDir ? { repoDir: pushRepoDir } : {}),
        },
      )
      setPullOutput(res.data.output ?? '')
      emitNotification(t('git.pulled', undefined, { defaultValue: 'Pulled successfully.' }), 'info')
      await loadGitStatus()
    } catch (error) {
      console.error('Pull failed', error)
      const data = (
        error as {
          response?: {
            data?: {
              output?: string
              error?: string
              errorKey?: string
              files?: string[]
            }
          }
        }
      ).response?.data
      if (data?.errorKey === 'git.error.pullConflict') {
        // The merge/rebase hit conflicts; the server already aborted and left the
        // tree untouched. Offer Synthase to resolve the listed files.
        const files = (data.files ?? []).join(', ')
        const message = t(
          'git.pullConflict',
          { files },
          {
            defaultValue:
              'Pull hit merge conflicts, so it was aborted and your work was left untouched. ' +
              'Synthase can help you resolve the conflicting files ({{files}}).',
          },
        )
        setPullOutput(data?.output ?? message)
        setPullFixPrompt(
          `My git pull${originUrl.trim() ? ` from ${originUrl.trim()}` : ''} hit merge conflicts` +
            (files ? ` in these files: ${files}` : '') +
            `. It was aborted so my work is intact. Use the git_pull tool (or help me resolve the ` +
            `conflicts) so I can get the remote changes without losing my work.`,
        )
        emitNotification(message, 'error')
      } else if (data?.errorKey === 'git.error.pullDiverged') {
        const message = t('git.pullDiverged', undefined, {
          defaultValue:
            'Pull could not fast-forward — your sandbox and the remote have both changed. ' +
            'Synthase can reconcile them with a merge or rebase.',
        })
        setPullOutput(data?.output ?? message)
        setPullFixPrompt(
          `My git pull${originUrl.trim() ? ` from ${originUrl.trim()}` : ''} could not fast-forward ` +
            `because my sandbox and the remote have diverged. Use the git_pull tool with a merge ` +
            `(keep both) or rebase strategy to reconcile them.`,
        )
        emitNotification(message, 'error')
      } else if (data?.errorKey === 'git.error.pullDirtyTree') {
        const message = t('git.pullDirtyTree', undefined, {
          defaultValue:
            'Pull blocked — you have uncommitted changes that would be overwritten. ' +
            'Commit or discard them, then pull again.',
        })
        setPullOutput(data?.output ?? message)
        emitNotification(message, 'error')
      } else if (data?.errorKey === 'git.error.originHostMismatch') {
        const message = t('git.originMismatch', undefined, {
          defaultValue:
            'That origin host is not one of your connected GitHub/GitLab providers. ' +
            'Pull only from your own repository, or use an SSH origin.',
        })
        setPullOutput(message)
        emitNotification(message, 'error')
      } else {
        const output = data?.output ?? data?.error ?? null
        setPullOutput(output)
        setPullFixPrompt(
          `My git pull${originUrl.trim() ? ` from ${originUrl.trim()}` : ''} failed.` +
            (output ? `\n\nOutput:\n${output}` : '') +
            `\n\nDiagnose the cause and fix it, then pull again with the git_pull tool. If it is an ` +
            `authentication or connection problem, tell me exactly what I need to do.`,
        )
        emitNotification(t('git.pullFailed', undefined, { defaultValue: 'Pull failed.' }), 'error')
      }
    }
    setPulling(false)
  }, [projectId, http, pulling, originUrl, pushRepoDir, t, loadGitStatus])

  // D6: the PANEL is reachable while the dev sandbox sleeps — deployment
  // history, the live URL, build logs and the domain records are control-plane
  // or production state. Only the two ACTIONS that read/write the container are
  // gated: build from the workspace tree (deploy) and push that tree to a remote.
  const canDeploy = sandboxStatus === 'running' && canEdit !== false
  const canPush = sandboxStatus === 'running' && canEdit !== false

  // Deploy builds IN the dev sandbox (runtime/production.ts runs `npm run build`
  // + reads/exports the tree there) and push reads the tree there too, so both
  // genuinely need the container awake. Rather than dead-end the buttons and send
  // the user out to the workspace shell to find the Wake control, offer the wake
  // right here when the sandbox is recoverable and the workspace handed us a way
  // to do it. `creating`/`queued` means the wake is already in flight — show it
  // as a loading button, not a second trigger.
  const sandboxAsleep =
    sandboxStatus === 'sleeping' || sandboxStatus === 'stopped' || sandboxStatus === 'lost'
  const sandboxBooting = sandboxStatus === 'creating' || sandboxStatus === 'queued'
  const showWake = !canDeploy && !!onWakeSandbox && (sandboxAsleep || sandboxBooting)

  /**
   * The in-panel wake button, shared by Deploy and Push (both need the sandbox
   * awake). While the boot is already in flight it shows loading + disabled.
   *
   * @param molId - The `data-mol-id` for this instance.
   * @param label - The action label (e.g. "Wake sandbox to deploy").
   * @returns The wake button.
   */
  const wakeButton = (molId: string, label: string): JSX.Element => (
    <Button
      data-mol-id={molId}
      size="sm"
      onClick={onWakeSandbox}
      disabled={sandboxBooting}
      loading={sandboxBooting}
      loadingText={t('deploy.wakingSandbox', undefined, { defaultValue: 'Starting sandbox...' })}
      style={{ height: 32 }}
    >
      {label}
    </Button>
  )

  /** One caption explaining why an action is blocked (asleep → why, booting → wait). */
  const wakeCaption =
    showWake && sandboxBooting
      ? t('deploy.sandboxStarting', undefined, {
          defaultValue: 'Starting the sandbox. This unlocks once it is running.',
        })
      : null

  // CICD1: honest caption beneath the hosting line. The molecule runtime is
  // the zero-config FREE default (and the ONLY live target — product decision
  // 2026-07-07: molecule hosts on its own servers only): label it and its
  // live-app limit (read from the tier, not guessed). The cap counts PROJECTS
  // running in production, not deploys — redeploying the same app never spends
  // another slot (see `checkDeploymentLimit`).
  const deployTargetCaption = usage
    ? t(
        'deploy.moleculeFreeCaption',
        { limit: formatLimit(usage.limits.maxActiveDeployments) },
        {
          defaultValue: `Free default — your app is hosted on a molecule.dev subdomain at no cost. Your plan runs ${formatLimit(usage.limits.maxActiveDeployments)} live at a time.`,
        },
      )
    : t('deploy.moleculeFreeCaptionGeneric', undefined, {
        defaultValue:
          'Free default — your app is hosted on a molecule.dev subdomain at no cost. Upgrade to scale and attach a custom domain.',
      })

  // The badge can only be turned off by a PAID account (the API refuses the
  // write for a free one, and the deploy-time rule would ignore it anyway —
  // a downgrade forces the badge back on). Until usage loads, render the
  // locked/free side: worst case a paid user sees the upgrade link for one
  // request's duration.
  const badgePaid = usage?.planCategory === 'pro' || usage?.planCategory === 'team'

  const patchBadge = useCallback(
    async (show: boolean) => {
      setBadgeEnabled(show)
      setBadgeSaving(true)
      try {
        // The server MERGES this partial bag into the stored settings.
        await http.patch(`/projects/${projectId}`, { settings: { attributionBadge: show } })
      } catch (error) {
        setBadgeEnabled(!show)
        emitNotification(
          t('deploy.attribution.saveFailed', undefined, {
            defaultValue: 'Could not save the badge setting.',
          }),
          'error',
        )
        console.error('Failed to save the badge setting', error)
      } finally {
        setBadgeSaving(false)
      }
    },
    [projectId, http, t],
  )

  const patchHosting = useCallback(
    async (next: 'auto' | 'static' | 'machine') => {
      const previous = hostingTarget
      setHostingTarget(next)
      setHostingSaving(true)
      try {
        // The server MERGES this partial bag into the stored settings.
        await http.patch(`/projects/${projectId}`, { settings: { hostingTarget: next } })
      } catch (error) {
        setHostingTarget(previous)
        emitNotification(
          t('deploy.hosting.saveFailed', undefined, {
            defaultValue: 'Could not save the hosting setting.',
          }),
          'error',
        )
        console.error('Failed to save the hosting setting', error)
      } finally {
        setHostingSaving(false)
      }
    },
    [projectId, http, t, hostingTarget],
  )

  const selectedProvider =
    authMethod === 'ssh' ? undefined : gitStatus?.providers?.find((p) => p.provider === authMethod)

  const sectionStyle = { marginTop: 24 } as const
  const headingStyle = { margin: '0 0 8px' } as const

  return (
    <div style={{ padding: 16, height: '100%', boxSizing: 'border-box', overflow: 'auto' }}>
      {/* ── Repository ─────────────────────────────────────────────────────── */}
      <section data-mol-id="repo-section">
        <h3 className={cm.sectionHeading} style={headingStyle}>
          {t('git.title', undefined, { defaultValue: 'Repository' })}
        </h3>
        <p className={cm.textMuted} style={{ margin: '0 0 12px', fontSize: 13 }}>
          {t('git.descCompact', undefined, {
            defaultValue:
              'Connect one of GitHub, GitLab or an SSH key, then push the project to your repo.',
          })}
        </p>

        {/* Compact method selector — the user picks ONE (GitHub / GitLab / SSH),
            and only that method's controls render below, so the three don't
            stack and eat vertical space. */}
        <div
          role="tablist"
          aria-label={t('git.method', undefined, { defaultValue: 'Authentication method' })}
          style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}
        >
          {AUTH_METHODS.map((method) => {
            const selected = authMethod === method
            const label =
              method === 'ssh'
                ? t('git.methodSsh', undefined, { defaultValue: 'SSH key' })
                : GIT_PROVIDER_LABELS[method]
            // Show connection state ON the selector so it is visible without
            // opening each method — the single-card view otherwise hides which
            // ones are connected.
            const methodConnected =
              method === 'ssh'
                ? Boolean(gitStatus?.ssh?.connected)
                : Boolean(gitStatus?.providers?.find((p) => p.provider === method)?.connected)
            return (
              <button
                key={method}
                type="button"
                role="tab"
                aria-selected={selected}
                data-mol-id={`git-method-${method}`}
                aria-label={
                  methodConnected
                    ? t(
                        'git.methodConnectedLabel',
                        { method: label },
                        { defaultValue: `${label} (connected)` },
                      )
                    : label
                }
                onClick={() => {
                  authMethodTouched.current = true
                  setAuthMethod(method)
                }}
                className={cm.cn(
                  selected
                    ? cm.button({ variant: 'solid', color: 'primary', size: 'sm' })
                    : cm.button({ variant: 'ghost', size: 'sm' }),
                )}
                style={{
                  minHeight: isMobile || coarse ? 40 : undefined,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {methodConnected && (
                  <span
                    data-mol-id={`git-method-connected-${method}`}
                    className={cm.textSuccess}
                    aria-hidden="true"
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: 'currentColor',
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                )}
                {label}
              </button>
            )
          })}
        </div>

        {/* Selected provider (GitHub / GitLab) connect card. */}
        {authMethod !== 'ssh' &&
          (selectedProvider ? (
            <div
              className={cm.borderAll}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                padding: '10px 12px',
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Real, full-color brand marks (GitHub / GitLab tanuki) via the
                    shared OAuthProviderLogo — decorative: the label + button carry
                    the accessible name. */}
                <OAuthProviderLogo provider={selectedProvider.provider} size={18} ariaLabel="" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className={cm.fontWeight('medium')} style={{ fontSize: 13 }}>
                    {GIT_PROVIDER_LABELS[selectedProvider.provider]}
                  </span>
                  {selectedProvider.connected ? (
                    <span className={cm.textSubtle} style={{ fontSize: 11 }}>
                      {selectedProvider.scopes.length > 0
                        ? t(
                            'git.scopes',
                            { scopes: selectedProvider.scopes.join(', ') },
                            { defaultValue: `Scopes: ${selectedProvider.scopes.join(', ')}` },
                          )
                        : t('git.connected', undefined, { defaultValue: 'Connected' })}
                    </span>
                  ) : !selectedProvider.configured ? (
                    <span className={cm.textSubtle} style={{ fontSize: 11 }}>
                      {t(
                        'git.unconfigured',
                        { provider: GIT_PROVIDER_LABELS[selectedProvider.provider] },
                        {
                          defaultValue: `This server has no ${GIT_PROVIDER_LABELS[selectedProvider.provider]} app configured. Ask the operator to set up a git provider app.`,
                        },
                      )}
                    </span>
                  ) : null}
                  {selectedProvider.connected && (
                    <Button
                      size="sm"
                      color="secondary"
                      data-mol-id={`git-disconnect-${selectedProvider.provider}`}
                      onClick={() => void handleDisconnect(selectedProvider.provider)}
                    >
                      {t('git.disconnect', undefined, { defaultValue: 'Disconnect' })}
                    </Button>
                  )}
                </div>
              </div>
              {canEdit !== false && (
                <Button
                  size="sm"
                  data-mol-id={`git-connect-${selectedProvider.provider}`}
                  onClick={() => handleConnect(selectedProvider.provider)}
                  disabled={!selectedProvider.configured}
                >
                  {selectedProvider.connected
                    ? t('git.reconnect', undefined, { defaultValue: 'Reconnect' })
                    : t(
                        'git.connect',
                        { provider: GIT_PROVIDER_LABELS[selectedProvider.provider] },
                        {
                          defaultValue: `Connect ${GIT_PROVIDER_LABELS[selectedProvider.provider]}`,
                        },
                      )}
                </Button>
              )}
            </div>
          ) : (
            <p className={cm.textSubtle} style={{ margin: '0 0 16px', fontSize: 12 }}>
              {t('common.loading', undefined, { defaultValue: 'Loading…' })}
            </p>
          ))}

        {/* SSH key method. */}
        {authMethod === 'ssh' && (
          <div style={{ marginBottom: 16 }}>
            <p className={cm.textMuted} style={{ margin: '0 0 8px', fontSize: 12 }}>
              {t('git.sshDesc', undefined, {
                defaultValue: 'Paste a PEM private key to push over SSH.',
              })}
            </p>
            {gitStatus?.ssh?.connected && gitStatus.ssh?.fingerprint && (
              <p
                className={cm.textSubtle}
                style={{
                  margin: '0 0 8px',
                  fontSize: 11,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                }}
              >
                {t(
                  'git.sshFingerprint',
                  { fingerprint: gitStatus.ssh.fingerprint },
                  { defaultValue: `Fingerprint: ${gitStatus.ssh.fingerprint}` },
                )}
              </p>
            )}
            <textarea
              data-mol-id="git-ssh-key-input"
              value={sshKeyInput}
              disabled={canEdit === false}
              onChange={(e) => setSshKeyInput(e.target.value)}
              placeholder={t('git.sshPlaceholder', undefined, {
                defaultValue: '-----BEGIN OPENSSH PRIVATE KEY-----',
              })}
              rows={4}
              spellCheck={false}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                // 16px on phones/touch — iOS zooms any focusable field below 16px.
                fontSize: isMobile || coarse ? 16 : 12,
                fontFamily: 'monospace',
                borderRadius: 6,
                border: '1px solid var(--color-border-secondary)',
                background: 'var(--color-surface)',
                color: 'inherit',
                outline: 'none',
                resize: 'vertical',
              }}
            />
            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {gitStatus?.ssh?.connected && (
                <Button
                  size="sm"
                  color="secondary"
                  data-mol-id="git-ssh-key-disconnect"
                  onClick={() => void handleDisconnect('ssh')}
                >
                  {t('git.disconnect', undefined, { defaultValue: 'Disconnect' })}
                </Button>
              )}
              {canEdit !== false && (
                <Button
                  size="sm"
                  color="secondary"
                  data-mol-id="git-ssh-key-save"
                  onClick={handleSaveSshKey}
                  disabled={savingSsh || !sshKeyInput.trim()}
                >
                  {t('git.sshSave', undefined, { defaultValue: 'Save SSH key' })}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Push — the repo URL + push button. Push reads the tree from the
            sandbox, so when it's asleep the button is replaced by the same wake
            affordance Deploy uses. */}
        <div>
          <h4 className={cm.fontWeight('semibold')} style={{ margin: '0 0 8px', fontSize: 13 }}>
            {t('git.push', undefined, { defaultValue: 'Push' })}
          </h4>
          {/* Wraps only when the row can't fit (phones): the origin input takes
              its own full-width line; repo picker + Push share the next one. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              data-mol-id="git-origin-input"
              value={originUrl}
              disabled={canEdit === false}
              onChange={(e) => {
                originSourceRef.current = 'user'
                setOriginUrl(e.target.value)
              }}
              placeholder={t('git.originPlaceholder', undefined, {
                defaultValue: 'https://github.com/you/your-repo.git',
              })}
              style={{
                flex: '1 1 220px',
                minWidth: 0,
                height: isMobile ? 40 : 32,
                boxSizing: 'border-box',
                padding: '6px 10px',
                // 16px on phones/touch — iOS zooms any focusable field below 16px.
                fontSize: inputFontSize,
                borderRadius: 6,
                border: '1px solid var(--color-border-secondary)',
                background: 'var(--color-surface)',
                color: 'inherit',
                outline: 'none',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canPush) handlePush()
              }}
            />
            {/* Repo picker: same treatment as the origin input — a real surface
                background so the native options popup isn't near-invisible in
                dark theme. */}
            {workspaceRepos.length > 1 && (
              <select
                value={pushRepoDir}
                onChange={(e) => setPushRepoDir(e.target.value)}
                data-mol-id="git-push-repo"
                aria-label={t('git.pushRepo', undefined, { defaultValue: 'Repository to push' })}
                style={{
                  height: isMobile ? 40 : 32,
                  boxSizing: 'border-box',
                  padding: '6px 8px',
                  fontSize: inputFontSize,
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: 6,
                  background: 'var(--color-surface)',
                  color: 'inherit',
                  outline: 'none',
                }}
              >
                {workspaceRepos.map((dir) => (
                  <option key={dir} value={dir}>
                    {dir}/
                  </option>
                ))}
              </select>
            )}
            {showWake ? (
              wakeButton(
                'git-wake-sandbox',
                t('git.wakeToPush', undefined, { defaultValue: 'Wake sandbox to push' }),
              )
            ) : (
              <>
                {canEdit !== false && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-mol-id="git-pull-button"
                    onClick={handlePull}
                    disabled={!canPush || pulling || pushing || !originUrl.trim()}
                    loading={pulling}
                    loadingText={t('git.pulling', undefined, { defaultValue: 'Pulling...' })}
                    style={{ height: isMobile ? 40 : 32 }}
                  >
                    {t('git.pull', undefined, { defaultValue: 'Pull' })}
                  </Button>
                )}
                {canEdit !== false && (
                  <Button
                    size="sm"
                    data-mol-id="git-push-button"
                    onClick={handlePush}
                    disabled={!canPush || pushing || pulling || !originUrl.trim()}
                    loading={pushing}
                    loadingText={t('git.pushing', undefined, { defaultValue: 'Pushing...' })}
                    style={{ height: isMobile ? 40 : 32 }}
                  >
                    {t('git.push', undefined, { defaultValue: 'Push' })}
                  </Button>
                )}
              </>
            )}
          </div>
          {!canPush && (
            <p
              data-mol-id="git-sandbox-required"
              className={cm.textSubtle}
              style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.5 }}
            >
              {showWake
                ? (wakeCaption ??
                  t('git.sandboxAsleepWake', undefined, {
                    defaultValue:
                      'Pushing reads your code from the dev sandbox, so it needs to wake first.',
                  }))
                : t('git.sandboxRequiredAction', undefined, {
                    defaultValue:
                      'Pushing needs a running sandbox — your code lives there. Start it from the workspace.',
                  })}
            </p>
          )}
          {pushOutput != null && (
            <pre
              className={cm.cn(cm.textMuted, cm.surface, cm.borderAll)}
              style={{
                marginTop: 12,
                padding: 12,
                maxHeight: 200,
                overflow: 'auto',
                fontSize: 11,
                lineHeight: 1.5,
                fontFamily: 'monospace',
                borderRadius: 6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {pushOutput || t('git.noOutput', undefined, { defaultValue: 'No output.' })}
            </pre>
          )}
          {pushRejected && onFixWithSynthase && (
            <div style={{ marginTop: 10 }}>
              <Button
                size="sm"
                color="primary"
                data-mol-id="git-push-resolve"
                onClick={handleResolvePush}
              >
                {t('git.resolveWithSynthase', undefined, {
                  defaultValue: 'Resolve with Synthase',
                })}
              </Button>
            </div>
          )}
          {pushFixPrompt && onFixWithSynthase && (
            <div style={{ marginTop: 10 }}>
              <Button
                size="sm"
                color="primary"
                data-mol-id="git-push-fix"
                onClick={() => onFixWithSynthase(pushFixPrompt)}
              >
                {t('git.fixWithSynthase', undefined, {
                  defaultValue: 'Fix with Synthase',
                })}
              </Button>
            </div>
          )}
          {pullOutput != null && (
            <pre
              data-mol-id="git-pull-output"
              className={cm.cn(cm.textMuted, cm.surface, cm.borderAll)}
              style={{
                marginTop: 12,
                padding: 12,
                maxHeight: 200,
                overflow: 'auto',
                fontSize: 11,
                lineHeight: 1.5,
                fontFamily: 'monospace',
                borderRadius: 6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {pullOutput || t('git.noOutput', undefined, { defaultValue: 'No output.' })}
            </pre>
          )}
          {pullFixPrompt && onFixWithSynthase && (
            <div style={{ marginTop: 10 }}>
              <Button
                size="sm"
                color="primary"
                data-mol-id="git-pull-fix"
                onClick={() => onFixWithSynthase(pullFixPrompt)}
              >
                {t('git.resolveWithSynthase', undefined, {
                  defaultValue: 'Resolve with Synthase',
                })}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ── Continuous deployment (CI/CD) — now a script, not a tab ─────────── */}
      <section data-mol-id="cicd-note-section" style={sectionStyle}>
        <h3 className={cm.sectionHeading} style={headingStyle}>
          {t('deploy.cicdTitle', undefined, { defaultValue: 'Continuous deployment' })}
        </h3>
        <p className={cm.textMuted} style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
          {t('deploy.cicdScriptNote', undefined, {
            defaultValue:
              'To build, test and deploy automatically on every push, run the “setup-ci” script from /scripts (or ask the AI). It adds a GitHub Actions or GitLab CI workflow to your repo — push it from above to enable it.',
          })}
        </p>
      </section>

      {/* ── Custom Domain (CICD1) — gated on the maxCustomDomains tier limit. */}
      <section data-mol-id="domain-tabpanel" style={sectionStyle}>
        <DomainConfig
          canEdit={canEdit}
          projectId={projectId}
          maxCustomDomains={usage ? usage.limits.maxCustomDomains : null}
        />
      </section>

      {/* ── Deploy ─────────────────────────────────────────────────────────── */}
      <section data-mol-id="deploy-section" style={sectionStyle}>
        <h3 className={cm.sectionHeading} style={headingStyle}>
          {t('deploy.title', undefined, { defaultValue: 'Deploy' })}
        </h3>
        {/* Hosting line — the first-party molecule runtime is the ONLY live
            target (molecule hosts on its own servers). No selector: one option is
            a statement, not a choice. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span className={cm.textMuted} style={{ fontSize: 13 }}>
            {t('deploy.target', undefined, { defaultValue: 'Hosting' })}
          </span>
          <span data-mol-id="deploy-target-molecule" style={{ fontSize: 13 }}>
            {t('deploy.targetMolecule', undefined, { defaultValue: 'Molecule (hosted)' })}
          </span>
          <span
            data-mol-id="deploy-default-tag"
            className={cm.cn(cm.textPrimary, cm.fontWeight('medium'))}
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 100,
              background: 'color-mix(in srgb, currentColor 15%, transparent)',
            }}
          >
            {t('deploy.defaultTag', undefined, { defaultValue: 'Included' })}
          </span>
        </div>

        {/* Honest free-default caption (CICD1). */}
        <p
          data-mol-id="deploy-target-caption"
          className={cm.textSubtle}
          style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5 }}
        >
          {deployTargetCaption}
        </p>

        {/* The "Built with Molecule.dev" badge. ON for every deploy; a paid
            plan can hide it per app (an account-wide default lives in Settings
            → Billing, and this per-app switch overrides it either way). The
            decision is re-made per deploy — runtime/attribution.ts — so a
            change lands at the next one. A free account gets the locked state
            with an upgrade link: /pricing is intercepted by the workspace's
            auth-modal mount, so it opens the plans modal in place. */}
        <div
          data-mol-id="deploy-attribution"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 6,
            border: '1px solid color-mix(in srgb, currentColor 18%, transparent)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {t('deploy.attribution.label', undefined, {
                defaultValue: '“Built with Molecule.dev” badge',
              })}
            </div>
            <div className={cm.textSubtle} style={{ fontSize: 12, lineHeight: 1.5, marginTop: 2 }}>
              {badgePaid
                ? t('deploy.attribution.descPaid', undefined, {
                    defaultValue:
                      'Shown bottom-right on your live app, linking visitors to molecule.dev. Turning it off takes effect at the next deploy.',
                  })
                : t('deploy.attribution.descFree', undefined, {
                    defaultValue:
                      'Shown bottom-right on your live app, linking visitors to molecule.dev. On a paid plan you can hide it.',
                  })}
            </div>
          </div>
          {badgePaid ? (
            <Switch
              data-mol-id="deploy-attribution-switch"
              checked={badgeEnabled ?? true}
              disabled={canEdit === false || badgeSaving}
              aria-label={t('deploy.attribution.label', undefined, {
                defaultValue: '“Built with Molecule.dev” badge',
              })}
              onChange={(e) => void patchBadge((e.target as HTMLInputElement).checked)}
            />
          ) : (
            <a
              href="/pricing"
              data-mol-id="deploy-attribution-upgrade"
              className={cm.cn(cm.textPrimary, cm.link, cm.fontWeight('semibold'))}
              style={{ fontSize: 12, whiteSpace: 'nowrap', textDecoration: 'underline' }}
            >
              {t('upgrade.viewPlans', undefined, { defaultValue: 'View plans' })}
            </a>
          )}
        </div>

        {/* Where the app is hosted. Automatic serves a static site as files (no
            server to start, no cold start) and runs anything with a server on a
            machine; the owner can pin either. Takes effect at the next deploy. */}
        <div
          data-mol-id="deploy-hosting"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 6,
            border: '1px solid color-mix(in srgb, currentColor 18%, transparent)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {t('deploy.hosting.label', undefined, { defaultValue: 'Hosting' })}
            </div>
            <div className={cm.textSubtle} style={{ fontSize: 12, lineHeight: 1.5, marginTop: 2 }}>
              {t('deploy.hosting.desc', undefined, {
                defaultValue:
                  'Automatic serves a static site as files and runs apps with a server on a machine. Changes apply at the next deploy.',
              })}
            </div>
          </div>
          <select
            data-mol-id="deploy-hosting-select"
            value={hostingTarget ?? 'auto'}
            disabled={canEdit === false || hostingSaving || hostingTarget === null}
            aria-label={t('deploy.hosting.label', undefined, { defaultValue: 'Hosting' })}
            onChange={(e) => void patchHosting(e.target.value as 'auto' | 'static' | 'machine')}
            style={{
              height: isMobile ? 40 : 32,
              boxSizing: 'border-box',
              padding: '6px 8px',
              fontSize: inputFontSize,
              border: '1px solid var(--color-border-secondary)',
              borderRadius: 6,
              background: 'var(--color-surface)',
              color: 'inherit',
              outline: 'none',
            }}
          >
            <option value="auto">
              {t('deploy.hosting.auto', undefined, { defaultValue: 'Automatic' })}
            </option>
            <option value="static">
              {t('deploy.hosting.static', undefined, { defaultValue: 'Static files' })}
            </option>
            <option value="machine">
              {t('deploy.hosting.machine', undefined, { defaultValue: 'Machine' })}
            </option>
          </select>
        </div>

        {/* Deploy action — no commit-message input (P-01): deploying never makes
            a git commit. When the sandbox is asleep, the Deploy button is replaced
            by a wake button (the build runs in the sandbox) rather than disabled. */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {showWake ? (
            wakeButton(
              'deploy-wake-sandbox',
              t('deploy.wakeToDeploy', undefined, { defaultValue: 'Wake sandbox to deploy' }),
            )
          ) : canEdit !== false ? (
            <Button
              data-mol-id="deploy-submit"
              size="sm"
              onClick={handleDeploy}
              disabled={!canDeploy || deploying}
              loading={deploying}
              loadingText={t('deploy.deploying', undefined, { defaultValue: 'Deploying...' })}
              style={{ height: 32 }}
            >
              {t('deploy.deployButton', undefined, { defaultValue: 'Deploy' })}
            </Button>
          ) : null}
        </div>

        {!canDeploy && (
          <p
            data-mol-id="deploy-sandbox-required"
            className={cm.textSubtle}
            style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}
          >
            {showWake
              ? (wakeCaption ??
                t('deploy.sandboxAsleepWake', undefined, {
                  defaultValue:
                    'Deploying builds your app in the dev sandbox, so it needs to wake first.',
                }))
              : t('deploy.sandboxRequiredAction', undefined, {
                  defaultValue: 'Deploying needs a running sandbox. Start it from the workspace.',
                })}
          </p>
        )}

        {/* CICD1: surface a rejected deploy (tier cap / guest) with a CTA — the
            same create-flow pattern — instead of a silently-dead button. */}
        {deployError && (
          <div
            data-mol-id="deploy-error"
            role="alert"
            className={cm.cn(deployError.cta ? cm.textWarning : cm.textError, cm.textSize('xs'))}
            style={{
              marginTop: 12,
              padding: '10px 12px',
              lineHeight: 1.5,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '1px solid color-mix(in srgb, currentColor 40%, transparent)',
              background: 'color-mix(in srgb, currentColor 12%, transparent)',
            }}
          >
            <span style={{ flex: 1 }}>{deployError.message}</span>
            {deployError.cta && (
              <a
                href={deployError.cta === 'signup' ? '/signup' : '/pricing'}
                data-mol-id="deploy-error-cta"
                className={cm.cn(cm.textPrimary, cm.link, cm.fontWeight('semibold'))}
                style={{ whiteSpace: 'nowrap', textDecoration: 'underline' }}
              >
                {deployError.cta === 'signup'
                  ? t('upgrade.signUp', undefined, { defaultValue: 'Sign up' })
                  : t('upgrade.viewPlans', undefined, { defaultValue: 'View plans' })}
              </a>
            )}
          </div>
        )}

        {/* Deployment history */}
        <div style={{ marginTop: 16 }}>
          <h4
            className={cm.cn(cm.textMuted, cm.fontWeight('medium'))}
            style={{ margin: '0 0 8px', fontSize: 13 }}
          >
            {t('deploy.history', undefined, { defaultValue: 'Deployment History' })}
          </h4>

          {loadError ? (
            <p className={cm.textError} style={{ fontSize: 13 }}>
              {loadError}
            </p>
          ) : deployments.length === 0 ? (
            <p className={cm.textSubtle} style={{ fontSize: 13 }}>
              {t('deploy.noDeployments', undefined, { defaultValue: 'No deployments yet.' })}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {deployments.map((dep) => {
                const colorClass = statusColorClass[dep.status] ?? cm.textPrimary
                const label = t(`deploy.status.${dep.status}`, undefined, {
                  defaultValue: STATUS_DEFAULT_LABELS[dep.status] ?? dep.status,
                })
                const isExpanded = expandedId === dep.id
                const isActive = ['pending', 'building', 'deploying'].includes(dep.status)

                return (
                  <div
                    key={dep.id}
                    className={cm.borderAll}
                    style={{ borderRadius: 8, overflow: 'hidden' }}
                  >
                    {/* Header — ONE line: status pill · timestamp · muted
                        description on the left; actions + chevron pinned right.
                        The description flexes and truncates so the row never
                        wraps and the time never reads like the app name. */}
                    <button
                      type="button"
                      // mol-bespoke-button: a deployment LIST ROW, not a CTA —
                      // the full-width expand/collapse hit area carrying the
                      // status/time/description chips. A button box would frame
                      // every row of the deployment history.
                      //
                      // The expanded detail polls GET /deployments/:depId, which
                      // is editor+ (it carries buildLog) — for viewers the row
                      // stays collapsed instead of showing 'No logs yet…' over a
                      // 403 poll every 3s. The list row already shows
                      // status/url/error at member level.
                      onClick={
                        canEdit === false
                          ? undefined
                          : () => setExpandedId(isExpanded ? null : dep.id)
                      }
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        background: 'transparent',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        fontSize: 13,
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {/* Status pill — colored by status. */}
                        <span
                          className={cm.cn(colorClass, cm.fontWeight('semibold'))}
                          style={{
                            ...pillStyle,
                            flexShrink: 0,
                            background: 'color-mix(in srgb, currentColor 15%, transparent)',
                          }}
                        >
                          {label}
                        </span>
                        {/* Time chip — its own hue: INFO (neutral metadata),
                            never a status/alarm color. Theme token, not hex. */}
                        <Tooltip content={new Date(dep.createdAt).toLocaleString()}>
                          <span
                            data-mol-id={`deploy-time-${dep.id}`}
                            className={cm.fontWeight('medium')}
                            style={{
                              ...pillStyle,
                              flexShrink: 0,
                              color: 'var(--color-info)',
                              background: 'color-mix(in srgb, var(--color-info) 14%, transparent)',
                            }}
                          >
                            {new Date(dep.createdAt).toLocaleString([], {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </span>
                        </Tooltip>
                        {/* Description chip — its own hue: TERTIARY accent (the
                            "what/who" label), distinct from status + time.
                            Theme token, truncates. */}
                        {dep.commitMessage && (
                          <span
                            className={cm.fontWeight('medium')}
                            style={{
                              ...pillStyle,
                              minWidth: 0,
                              flexShrink: 1,
                              overflow: 'hidden',
                              color: 'var(--color-tertiary)',
                              background:
                                'color-mix(in srgb, var(--color-tertiary) 16%, transparent)',
                            }}
                          >
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                            >
                              {dep.commitMessage}
                            </span>
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexShrink: 0,
                        }}
                      >
                        {dep.url && (
                          <a
                            href={dep.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={cm.cn(cm.textPrimary, cm.link)}
                            style={{
                              fontSize: 12,
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <Icon name="link-external" size={12} aria-hidden="true" />
                            {t('deploy.viewSite', undefined, { defaultValue: 'View Site' })}
                          </a>
                        )}
                        {isActive &&
                          (canEdit !== false ? (
                            <Button
                              size="sm"
                              color="error"
                              data-mol-id={`deploy-cancel-${dep.id}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCancel(dep.id)
                              }}
                            >
                              {t('deploy.cancel', undefined, { defaultValue: 'Cancel' })}
                            </Button>
                          ) : null)}
                        {dep.status === 'failed' && onFixWithSynthase && (
                          <Button
                            size="sm"
                            color="primary"
                            data-mol-id={`deploy-fix-${dep.id}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleFixDeploy(dep)
                            }}
                          >
                            {t('deploy.fixWithAi', undefined, {
                              defaultValue: 'Fix with Synthase',
                            })}
                          </Button>
                        )}
                        <Icon
                          name={isExpanded ? 'chevron-up' : 'chevron-down'}
                          size={14}
                          className={cm.textSubtle}
                          aria-hidden="true"
                        />
                      </div>
                    </button>

                    {/* Expanded log */}
                    {isExpanded && (
                      <div
                        className={cm.borderT}
                        style={{ padding: 12, maxHeight: 200, overflow: 'auto' }}
                      >
                        {dep.error && (
                          <p className={cm.textError} style={{ margin: '0 0 8px', fontSize: 12 }}>
                            {dep.error}
                          </p>
                        )}
                        <pre
                          className={cm.textMuted}
                          style={{
                            margin: 0,
                            fontSize: 11,
                            lineHeight: 1.5,
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                          }}
                        >
                          {logsById[dep.id] ||
                            dep.buildLog ||
                            t('deploy.noLogs', undefined, { defaultValue: 'No logs yet...' })}
                        </pre>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
